// ============================================================
// CLUBHUB – Nachrichten: wie es beim Empfaenger ankommt
// ============================================================
// Drei Vorschauen, je Kanal eine — gezeichnet mit genau den Werten
// EINER Person (Platzhalter schon eingesetzt).
//
// DIE MAIL hat EIN Geruest fuer alles, was der Verein verschickt,
// wie im Agent (`lib/email.ts`, `shell()`): ruhiger Grund, oben der
// Vereinskopf, eine Karte mit Vorspann, Betreff als Titel und Text,
// darunter der Grund, warum man die Mail bekommt, und die
// Pflichtangaben des Vereins. Ein Newsletter traegt dort zusaetzlich
// den Abmeldelink — Pflicht, und bei ClubDesk nur ein Textbaustein.
//
// Ohne Zustand: laeuft im Editor und im Versandprotokoll.
// ============================================================
import { Paperclip } from "lucide-react";
import { cn } from "@/lib/utils";
import { alsText } from "@/lib/nachrichten/markdown";
import { smsLaenge } from "@/lib/nachrichten/sms";
import type { AnhangView, VereinsangabenView, Zweck } from "@/lib/nachrichten/views";
import { MarkdownText } from "./markdown-text";

export function dateigroesse(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

function Vereinszeichen({ verein, klein = false }: { verein: VereinsangabenView; klein?: boolean }) {
  return (
    <span
      className={cn(
        "bg-accent text-accent-contrast grid shrink-0 place-items-center rounded-md font-semibold tracking-wide",
        klein ? "h-6 w-6 text-[10px]" : "h-8 w-8 text-[11px]",
      )}
      aria-hidden
    >
      {verein.initials}
    </span>
  );
}

function Pflichtangaben({ verein }: { verein: VereinsangabenView }) {
  return (
    <p>
      {verein.name} · {verein.street} · {verein.zip} {verein.city} · {verein.register}
      {verein.vorstand.length > 0 && <> · Vertreten durch {verein.vorstand.join(", ")}</>}
    </p>
  );
}

// ── E-Mail ──────────────────────────────────────────────────

export function MailVorschau({
  verein,
  von,
  antwortAn,
  an,
  betreff,
  text,
  zweck,
  anhaenge,
  geraet = "desktop",
}: {
  verein: VereinsangabenView;
  von: string;
  antwortAn: string | null;
  /** „Lena Huber <lena.huber@…>“ */
  an: string;
  betreff: string;
  /** Markdown, Platzhalter eingesetzt. */
  text: string;
  zweck: Zweck;
  anhaenge: AnhangView[];
  geraet?: "desktop" | "telefon";
}) {
  const telefon = geraet === "telefon";
  return (
    <div className={cn("mx-auto w-full", telefon ? "max-w-[390px]" : "max-w-[640px]")}>
      <div
        className={cn(
          "border-border bg-bg-primary overflow-hidden border",
          telefon ? "rounded-[28px] p-2 shadow-e2" : "rounded-xl",
        )}
      >
        {/* Der Posteingang: so steht die Mail in der Liste des Empfaengers. */}
        <dl className={cn("text-caption border-border-subtle grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 border-b px-4 py-3", telefon && "rounded-t-[20px]")}>
          <dt className="text-text-tertiary">Von</dt>
          <dd className="text-text-primary truncate">
            {von} <span className="text-text-tertiary">&lt;{verein.absenderAdresse}&gt;</span>
          </dd>
          {antwortAn && (
            <>
              <dt className="text-text-tertiary">Antwort an</dt>
              <dd className="text-text-primary truncate">{antwortAn}</dd>
            </>
          )}
          <dt className="text-text-tertiary">An</dt>
          <dd className="text-text-primary truncate">{an}</dd>
          <dt className="text-text-tertiary">Betreff</dt>
          <dd className="text-text-primary truncate font-medium">{betreff || <span className="text-text-tertiary font-normal">Ohne Betreff</span>}</dd>
        </dl>

        {/* Das Geruest der Mail. */}
        <div className={cn("bg-bg-secondary", telefon ? "rounded-b-[20px] px-3 pt-5 pb-6" : "px-6 pt-7 pb-8")}>
          <div className="mb-4 flex items-center gap-2.5 px-1">
            <Vereinszeichen verein={verein} klein={telefon} />
            <span className="text-overline text-text-secondary truncate">{verein.shortName}</span>
          </div>

          <div className={cn("bg-bg-sheet border-border rounded-xl border", telefon ? "px-5 py-6" : "px-9 py-8")}>
            <p className="text-overline text-text-tertiary">{zweck === "NEWSLETTER" ? "Vereinsbrief" : "Vereinsinformation"}</p>
            <h1 className={cn("text-text-primary mt-2 font-semibold", telefon ? "text-heading-md" : "text-heading-lg")}>
              {betreff || "Ohne Betreff"}
            </h1>
            <div className="text-body-md text-text-primary mt-5">
              {text.trim() ? <MarkdownText text={text} /> : <p className="text-text-tertiary">Hier steht dein Text.</p>}
            </div>

            {anhaenge.length > 0 && (
              <ul className="border-border-subtle mt-6 space-y-1.5 border-t pt-4">
                {anhaenge.map((a) => (
                  <li key={a.id} className="text-body-sm flex items-center gap-2">
                    <Paperclip className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span className="text-text-primary min-w-0 truncate">{a.name}</span>
                    <span className="text-caption text-text-tertiary shrink-0 tabular-nums">{dateigroesse(a.size)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="text-caption text-text-tertiary mt-5 space-y-2 px-1">
            {zweck === "NEWSLETTER" ? (
              <p>
                Du bekommst den Vereinsbrief, weil du eingewilligt hast.{" "}
                <span className="text-text-secondary underline underline-offset-2">Mit einem Klick abmelden</span>
              </p>
            ) : (
              <p>Du bekommst diese Nachricht als Mitglied des {verein.name}.{antwortAn ? ` Antworten gehen an ${antwortAn}.` : ""}</p>
            )}
            <Pflichtangaben verein={verein} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SMS ─────────────────────────────────────────────────────

export function SmsVorschau({ verein, text }: { verein: VereinsangabenView; text: string }) {
  const rein = alsText(text);
  const l = smsLaenge(rein);
  return (
    <div className="mx-auto w-full max-w-[360px]">
      <div className="border-border bg-bg-primary shadow-e2 overflow-hidden rounded-[28px] border p-2">
        <div className="bg-bg-secondary rounded-[20px] px-4 pt-4 pb-8">
          <p className="text-caption text-text-tertiary text-center">Absender</p>
          <p className="text-body-sm text-text-primary text-center font-medium">{verein.smsKennung}</p>
          <div className="mt-6 flex">
            <div className="bg-bg-tertiary text-body-sm text-text-primary max-w-[85%] rounded-2xl rounded-bl-md px-3.5 py-2.5 whitespace-pre-wrap">
              {rein.trim() || <span className="text-text-tertiary">Hier steht deine SMS.</span>}
            </div>
          </div>
        </div>
      </div>
      <p className="text-caption text-text-tertiary mt-3 text-center tabular-nums">
        {l.zeichen} Zeichen · {l.teile === 1 ? "eine SMS" : `${l.teile} Teile`} · {l.unicode ? "Unicode" : "Standardzeichen"}
      </p>
    </div>
  );
}

// ── Brief ───────────────────────────────────────────────────

export function BriefVorschau({
  verein,
  empfaenger,
  betreff,
  text,
  absender,
  datum,
  anhaenge,
}: {
  verein: VereinsangabenView;
  /** Zeilen des Anschriftfelds. */
  empfaenger: string[];
  betreff: string;
  text: string;
  /** Wer unterschreibt. */
  absender: string;
  /** „23. Oktober 2026“ */
  datum: string;
  anhaenge: AnhangView[];
}) {
  return (
    <div className="mx-auto w-full max-w-[560px]">
      <div className="bg-bg-sheet border-border shadow-e2 relative flex aspect-[210/297] flex-col overflow-hidden rounded-md border px-[9%] pt-[7%] pb-[5%]">
        <div className="flex items-center justify-end gap-2">
          <span className="text-caption text-text-secondary text-right leading-tight font-medium">{verein.name}</span>
          <Vereinszeichen verein={verein} klein />
        </div>
        <div className="mt-[9%] w-[58%]">
          <p className="border-border-subtle text-text-tertiary truncate border-b pb-0.5 text-[9px]">
            {verein.name} · {verein.street} · {verein.zip} {verein.city}
          </p>
          <div className="text-body-sm text-text-primary mt-1.5 leading-snug">
            {empfaenger.map((z, i) => (
              <p key={i} className="truncate">
                {z}
              </p>
            ))}
          </div>
        </div>
        <p className="text-caption text-text-secondary mt-[7%] text-right tabular-nums">
          {verein.city}, {datum}
        </p>
        <p className="text-body-sm text-text-primary mt-3 font-semibold">{betreff || "Betreff"}</p>
        <div className="text-body-sm text-text-primary mt-3 min-h-0 flex-1 overflow-hidden">
          {text.trim() ? <MarkdownText text={text} className="space-y-2" /> : <p className="text-text-tertiary">Hier steht dein Text.</p>}
          <p className="mt-4">Viele Grüße</p>
          <p className="mt-1">{absender}</p>
          {anhaenge.length > 0 && (
            <p className="text-caption text-text-secondary mt-4">
              Anlage{anhaenge.length > 1 ? "n" : ""}: {anhaenge.map((a) => a.name).join(", ")}
            </p>
          )}
        </div>
        <div className="border-border-subtle text-text-tertiary mt-3 border-t pt-1.5 text-[9px] leading-snug">
          <Pflichtangaben verein={verein} />
        </div>
      </div>
    </div>
  );
}
